import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useSchool } from '../context/SchoolContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { 
  Plus, Search, MoreVertical, Trash2, FolderOpen, Archive, ArchiveRestore, ChevronDown, ChevronUp, Pencil
} from 'lucide-react';
import { toast } from 'sonner';
import { API_BASE } from '../apiConfig';

const API = API_BASE;

const classColors = [
  'class-color-1', 'class-color-2', 'class-color-3',
  'class-color-4', 'class-color-5', 'class-color-6',
];

export const Dashboard = () => {
  const { selectedSchool } = useSchool();
  const navigate = useNavigate();
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [newClass, setNewClass] = useState({ name: '', class_code: '', grade: '', subject: '' });
  const [editingClass, setEditingClass] = useState(null);
  const [editClass, setEditClass] = useState({ name: '', class_code: '', grade: '', subject: '' });
  const [savingEdit, setSavingEdit] = useState(false);

  const fetchClasses = async () => {
    if (!selectedSchool) return;
    try {
      const response = await fetch(`${API}/classes?school_id=${selectedSchool.school_id}`, { credentials: 'include' });
      if (response.ok) setClasses(await response.json());
    } catch (error) { console.error('Failed to fetch classes:', error); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchClasses(); }, [selectedSchool]);

  const handleCreateClass = async () => {
    if (!newClass.name.trim()) { toast.error('Class name is required'); return; }
    try {
      const response = await fetch(`${API}/classes`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ ...newClass, school_id: selectedSchool.school_id })
      });
      if (response.ok) { toast.success('Class created'); setDialogOpen(false); setNewClass({ name: '', class_code: '', grade: '', subject: '' }); fetchClasses(); }
      else toast.error('Failed to create class');
    } catch (error) { toast.error('Failed to create class'); }
  };

  const handleDeleteClass = async (e, classId) => {
    e.preventDefault(); e.stopPropagation();
    if (!window.confirm('Delete this class and all its data?')) return;
    try {
      const response = await fetch(`${API}/classes/${classId}`, { method: 'DELETE', credentials: 'include' });
      if (response.ok) { toast.success('Class deleted'); fetchClasses(); }
      else toast.error('Failed to delete class');
    } catch (error) { toast.error('Failed to delete class'); }
  };

  const handleArchiveClass = async (e, classId) => {
    e.preventDefault(); e.stopPropagation();
    try {
      const response = await fetch(`${API}/classes/${classId}/archive`, { method: 'PUT', credentials: 'include' });
      if (response.ok) { toast.success('Class archived'); fetchClasses(); }
      else toast.error('Failed to archive class');
    } catch (error) { toast.error('Failed to archive class'); }
  };

  const handleUnarchiveClass = async (e, classId) => {
    e.preventDefault(); e.stopPropagation();
    try {
      const response = await fetch(`${API}/classes/${classId}/unarchive`, { method: 'PUT', credentials: 'include' });
      if (response.ok) { toast.success('Class restored'); fetchClasses(); }
      else toast.error('Failed to restore class');
    } catch (error) { toast.error('Failed to restore class'); }
  };

  const handleEditClass = (e, cls) => {
    e.preventDefault(); e.stopPropagation();
    setEditingClass(cls);
    setEditClass({ name: cls.name, class_code: cls.class_code || '', grade: cls.grade || '', subject: cls.subject || '' });
  };

  const handleRenameClass = async () => {
    if (!editClass.name.trim()) { toast.error('Class name is required'); return; }
    setSavingEdit(true);
    try {
      const response = await fetch(`${API}/classes/${editingClass.class_id}/rename`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify(editClass)
      });
      if (response.ok) { toast.success('Class updated'); setEditingClass(null); fetchClasses(); }
      else toast.error('Failed to update class');
    } catch (error) { toast.error('Failed to update class'); }
    finally { setSavingEdit(false); }
  };

  const activeClasses = classes.filter(cls => !cls.archived);
  const archivedClasses = classes.filter(cls => cls.archived);

  const filterClasses = (list) => list.filter(cls => {
    const q = searchQuery.toLowerCase();
    return cls.name.toLowerCase().includes(q) ||
      (cls.class_code && cls.class_code.toLowerCase().includes(q)) ||
      (cls.grade && cls.grade.toLowerCase().includes(q)) ||
      (cls.subject && cls.subject.toLowerCase().includes(q));
  });

  const filteredActive = filterClasses(activeClasses);
  const filteredArchived = filterClasses(archivedClasses);

  const renderClassCard = (cls, index, isArchived = false) => (
    <Card
      key={cls.class_id}
      className={`bg-white border border-slate-200 overflow-hidden animate-fade-in group cursor-pointer rounded-xl ${
        isArchived ? 'opacity-60' : 'hover:shadow-google-card hover:-translate-y-0.5'
      }`}
      style={{ animationDelay: `${index * 40}ms`, transition: 'transform 0.2s, box-shadow 0.2s' }}
      onClick={() => !isArchived && navigate(`/app/students/${cls.class_id}`)}
      data-testid={`class-card-${cls.class_id}`}
    >
      <CardContent className="p-0 flex items-stretch">
        {/* Slim color accent */}
        <div className={`w-1.5 flex-shrink-0 ${isArchived ? 'bg-slate-300' : classColors[index % classColors.length].replace('background: linear-gradient', '')}`}
          style={isArchived ? {} : { background: ['#4338ca','#15803d','#b45309','#be123c','#7c3aed','#0e7490'][index % 6] }} />
        <div className="flex-1 px-4 py-3 flex items-center justify-between min-w-0">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-slate-800 truncate">{cls.name}</h3>
            <p className="text-xs text-slate-500 mt-0.5 truncate">
              {cls.class_code}{cls.class_code && (cls.grade || cls.subject) ? ' \u00B7 ' : ''}{cls.grade}{cls.grade && cls.subject ? ' \u00B7 ' : ''}{cls.subject}
            </p>
            {isArchived && <span className="inline-block mt-1 px-1.5 py-0.5 bg-slate-100 rounded text-[10px] text-slate-500">Archived</span>}
          </div>
          <div className="opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 ml-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors"
                  onClick={(e) => e.stopPropagation()}
                  data-testid={`class-menu-${cls.class_id}`}
                >
                  <MoreVertical className="w-4 h-4 text-slate-400" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem className="cursor-pointer" onClick={(e) => handleEditClass(e, cls)} data-testid={`edit-btn-${cls.class_id}`}>
                  <Pencil className="w-4 h-4 mr-2" />Edit
                </DropdownMenuItem>
                {isArchived ? (
                  <DropdownMenuItem className="cursor-pointer" onClick={(e) => handleUnarchiveClass(e, cls.class_id)} data-testid={`unarchive-btn-${cls.class_id}`}>
                    <ArchiveRestore className="w-4 h-4 mr-2" />Restore
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem className="cursor-pointer" onClick={(e) => handleArchiveClass(e, cls.class_id)} data-testid={`archive-btn-${cls.class_id}`}>
                    <Archive className="w-4 h-4 mr-2" />Archive
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem className="text-rose-600 cursor-pointer" onClick={(e) => handleDeleteClass(e, cls.class_id)} data-testid={`delete-btn-${cls.class_id}`}>
                  <Trash2 className="w-4 h-4 mr-2" />Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <Layout breadcrumb={[{ label: 'Classes' }]}>
      <div className="space-y-6" data-testid="dashboard">
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="relative flex-1 max-w-md w-full">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Search classes..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-10 border-slate-200 bg-white rounded-xl text-sm"
              data-testid="search-classes-input" />
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl h-10 px-5 shadow-sm text-sm font-medium" data-testid="add-class-btn">
                <Plus className="w-4 h-4 mr-2" />Create Class
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white rounded-2xl">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl text-slate-900">Create class</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-3">
                <div>
                  <label className="text-sm font-medium text-slate-600">Class name *</label>
                  <Input placeholder="e.g., Year 5 Blue" value={newClass.name} onChange={(e) => setNewClass({ ...newClass, name: e.target.value })} className="mt-1.5 border-slate-200 rounded-xl" data-testid="class-name-input" />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-600">Class code</label>
                  <Input placeholder="e.g., Y5B" value={newClass.class_code} onChange={(e) => setNewClass({ ...newClass, class_code: e.target.value })} className="mt-1.5 border-slate-200 rounded-xl" data-testid="class-code-input" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium text-slate-600">Grade</label>
                    <Input placeholder="e.g., Year 5" value={newClass.grade} onChange={(e) => setNewClass({ ...newClass, grade: e.target.value })} className="mt-1.5 border-slate-200 rounded-xl" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-600">Subject</label>
                    <Input placeholder="e.g., English" value={newClass.subject} onChange={(e) => setNewClass({ ...newClass, subject: e.target.value })} className="mt-1.5 border-slate-200 rounded-xl" />
                  </div>
                </div>
                <div className="flex justify-end gap-3 pt-2">
                  <Button variant="ghost" onClick={() => setDialogOpen(false)} className="text-slate-500 hover:bg-slate-50 rounded-xl">Cancel</Button>
                  <Button onClick={handleCreateClass} className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl" data-testid="create-class-btn">Create</Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="text-center py-16 text-slate-400">Loading...</div>
        ) : activeClasses.length === 0 && archivedClasses.length === 0 ? (
          <Card className="bg-white border border-dashed border-slate-300 shadow-none rounded-2xl">
            <CardContent className="py-16 text-center">
              <FolderOpen className="w-14 h-14 text-slate-200 mx-auto mb-4" />
              <h3 className="text-lg font-heading font-semibold text-slate-700 mb-2">No classes yet</h3>
              <p className="text-slate-400 text-sm mb-6">Create your first class to get started</p>
              <Button onClick={() => setDialogOpen(true)} className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl">
                <Plus className="w-4 h-4 mr-2" />Create class
              </Button>
            </CardContent>
          </Card>
        ) : filteredActive.length === 0 && searchQuery ? (
          <Card className="bg-white border border-slate-200 shadow-none rounded-2xl">
            <CardContent className="py-12 text-center">
              <Search className="w-10 h-10 text-slate-200 mx-auto mb-3" />
              <h3 className="text-base font-medium text-slate-700 mb-1">No results found</h3>
              <p className="text-slate-400 text-sm">Try a different search</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {filteredActive.map((cls, index) => renderClassCard(cls, index, false))}
          </div>
        )}

        {archivedClasses.length > 0 && (
          <div className="pt-4" data-testid="archived-section">
            <button
              onClick={() => setShowArchived(!showArchived)}
              className="flex items-center gap-2 text-slate-500 hover:text-slate-700 transition-colors mb-4 text-sm"
              data-testid="toggle-archived-btn"
            >
              <Archive className="w-4 h-4" />
              <span className="font-medium">Archived ({archivedClasses.length})</span>
              {showArchived ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>
            {showArchived && (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 animate-fade-in">
                {filteredArchived.map((cls, index) => renderClassCard(cls, index, true))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Edit Class Dialog */}
      <Dialog open={!!editingClass} onOpenChange={(open) => { if (!open) setEditingClass(null); }}>
        <DialogContent className="bg-white rounded-2xl" data-testid="edit-class-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-slate-900">Edit class</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-3">
            <div>
              <label className="text-sm font-medium text-slate-600">Class name *</label>
              <Input value={editClass.name} onChange={(e) => setEditClass({ ...editClass, name: e.target.value })} className="mt-1.5 border-slate-200 rounded-xl" data-testid="edit-class-name-input" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-600">Class code</label>
              <Input value={editClass.class_code} onChange={(e) => setEditClass({ ...editClass, class_code: e.target.value })} className="mt-1.5 border-slate-200 rounded-xl" data-testid="edit-class-code-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-slate-600">Grade</label>
                <Input value={editClass.grade} onChange={(e) => setEditClass({ ...editClass, grade: e.target.value })} className="mt-1.5 border-slate-200 rounded-xl" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-600">Subject</label>
                <Input value={editClass.subject} onChange={(e) => setEditClass({ ...editClass, subject: e.target.value })} className="mt-1.5 border-slate-200 rounded-xl" />
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="ghost" onClick={() => setEditingClass(null)} className="text-slate-500 hover:bg-slate-50 rounded-xl">Cancel</Button>
              <Button onClick={handleRenameClass} disabled={savingEdit || !editClass.name.trim()} className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl" data-testid="save-class-edit-btn">
                {savingEdit ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};
